import { CoreTextView } from '@core/widgets';
import { useBehavior } from '@libs/core';
import { TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { Activity } from 'react';
import { SemanticColor, semanticColors } from '../../theme/colors';
import { TReferralsProgressData } from '../../types/data';
import { TextStyle } from '../../types/styles';
import { getInlineStyles } from '../../utils/styles';
import styles from './referralsProgress.module.css';

export const ReferralsProgress: React.FC<TWidgetProps<TReferralsProgressData>> = ({ widget }) => {
    const { earnedAmount = 0, totalAmount = 0, totalSteps = 0 } = widget.data?.value ?? {};
    const { referralTitle, startSubtitle, progressSubtitle } = widget.data?.messages ?? {};
    const { margin } = widget.options ?? {};
    const { isHidden } = useBehavior(widget);

    const completedSteps = totalSteps > 0 ? Math.floor((earnedAmount / totalAmount) * totalSteps) : 0;
    const hasProgress = earnedAmount > 0;
    const fillPercent = totalSteps > 1 && completedSteps > 0 ? ((completedSteps - 1) / (totalSteps - 1)) * 100 : 0;

    const amountColor = semanticColors[hasProgress ? SemanticColor.TextEmphasised : SemanticColor.TextSecondary];

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div className={styles.container} style={getInlineStyles({ margin })} data-test={widget.uid}>
                <div className={styles.header}>
                    {referralTitle && <CoreTextView text={referralTitle} styleProps={{ ...TextStyle.main, main: semanticColors[SemanticColor.TextPrimary] }} />}
                    {progressSubtitle && <CoreTextView text={progressSubtitle} styleProps={{ ...TextStyle.main, main: amountColor, textAlign: 'end' }} />}
                </div>
                <div className={styles.progressTrack} data-test="progress-track">
                    <div className={styles.progressFill} data-test="progress-fill" style={{ width: `${fillPercent}%` }} />
                    {Array.from({ length: totalSteps }, (_, i) => (
                        <div
                            key={i}
                            data-test={i < completedSteps ? 'dot-active' : 'dot-inactive'}
                            className={clsx(styles.dot, i < completedSteps ? styles.dotActive : styles.dotInactive)}
                        />
                    ))}
                </div>
                {startSubtitle && (
                    <CoreTextView text={startSubtitle} styleProps={{ ...TextStyle.secondary, main: semanticColors[SemanticColor.TextPrimary] }} />
                )}
            </div>
        </Activity>
    );
};
